import { useState, type ReactElement } from 'react';
import { workerRequestPacketText, type WorkerRequestBlock } from '../lib/worker-request-blocks';
import '../worker-request-blocks.css';

type CopyState = { readonly key: string; readonly ok: boolean } | null;

/**
 * #561 copy-ready Worker requests for one approved plan. Copying writes the
 * clipboard only: no task, assignment, run, approval, dispatch, or terminal write.
 */
export function WorkerRequestBlocks({ blocks }: { readonly blocks: readonly WorkerRequestBlock[] }): ReactElement | null {
  const [copied, setCopied] = useState<CopyState>(null);
  if (blocks.length === 0) return null;

  const copy = async (key: string, text: string) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      ok = false;
    }
    setCopied({ key, ok });
    window.setTimeout(() => setCopied((current) => (current?.key === key ? null : current)), 2000);
  };
  const copyLabel = (key: string, idle: string) => (copied?.key === key ? (copied.ok ? 'Copied' : 'Copy failed') : idle);

  return (
    <details className="worker-request-blocks" data-testid="worker-request-blocks">
      <summary>
        Copy-ready Worker requests ({blocks.length})
      </summary>
      <p className="worker-request-note muted">
        Manual dispatch bridge. Copying creates no task, assignment, run, or dispatch.
        Offline or unusable Workers are never named as a destination.
      </p>
      <div className="row wrap worker-request-toolbar">
        <button type="button" onClick={() => void copy('all', workerRequestPacketText(blocks))}>
          {copyLabel('all', 'Copy all')}
        </button>
      </div>
      <ol className="worker-request-list">
        {blocks.map((block) => {
          const key = `task:${block.taskIndex}`;
          return (
            <li key={block.taskIndex} className="worker-request-block" aria-label={`Request ${block.taskIndex + 1}: ${block.designation}`}>
              <div className="row spread wrap worker-request-head">
                <strong className="worker-request-designation">
                  {block.taskIndex + 1}. {block.designation}
                </strong>
                <button type="button" onClick={() => void copy(key, block.text)}>
                  {copyLabel(key, 'Copy')}
                </button>
              </div>
              <div className="row wrap worker-request-badges">
                <span className="tag">
                  {block.dependencyTaskIndexes.length === 0
                    ? 'No dependencies'
                    : `After ${block.dependencyTaskIndexes.map((index) => index + 1).join(', ')}`}
                </span>
                <span className="tag">
                  {block.parallelTaskIndexes.length === 0
                    ? 'Not proven parallel'
                    : `Parallel with ${block.parallelTaskIndexes.map((index) => index + 1).join(', ')}`}
                </span>
              </div>
              <pre className="worker-request-text">{block.text}</pre>
            </li>
          );
        })}
      </ol>
      {copied !== null && (
        <span role="status" className="muted worker-request-status">
          {copied.ok ? 'Copied to clipboard.' : 'Clipboard is unavailable; select the text to copy it.'}
        </span>
      )}
    </details>
  );
}
